// L'accès à l'application, décidé par le SERVEUR et seulement lu ici.
//
// Neuf écrans recomposaient cette décision eux-mêmes, à partir de trois booléens et d'une
// comparaison de dates en JavaScript. Chacun avait sa propre version de la règle : c'est
// ainsi qu'un membre d'équipe se faisait mettre dehors à la fin de SON essai pendant que son
// entreprise payait, et qu'une boucle de redirections s'était installée entre « / » et
// « /renewal ». La règle vit maintenant à un seul endroit (Organization#access_state), et
// ce fichier ne fait que la lire.
//
// La charge servie ressemble à :
//   { allowed, destination, state, inTrial, trialEndsOn, paymentFailed,
//     canSubscribe, canRenew, canCancel, hasBillingHistory }
// où `destination` vaut null, 'subscribe' ou 'renewal'.

const EMPTY = {};

export const accessOf = (user) => (user && user.access) || EMPTY;

// ⚠️ En l'absence de l'objet, on laisse entrer. Le cas se produit sur une charge
// d'utilisateur d'un onglet resté ouvert avant la mise en ligne : refuser l'entrée
// mettrait dehors des clients qui paient, pour une clé manquante. Le serveur, lui, refuse
// de toute façon chaque appel — cette barrière-ci n'est qu'un confort de navigation.
export const canEnter = (user) => accessOf(user).allowed !== false;

// Où envoyer quelqu'un à qui l'accès est fermé. Null tant qu'il est ouvert.
export const accessRedirect = (user) => {
  const { destination } = accessOf(user);
  if (destination === 'subscribe') return '/subscribe';
  if (destination === 'renewal') return '/renewal';
  return null;
};

export const isInTrial = (user) => accessOf(user).inTrial === true;

// Un paiement n'est pas passé, et l'accès reste pourtant ouvert : Stripe relance la carte
// pendant environ trois semaines avant d'abandonner. C'est le seul état où l'application
// fonctionne normalement alors que l'abonnement est en train de mourir — d'où le bandeau,
// puisque rien à l'écran ne le laisserait deviner.
//
// ⚠️ Le drapeau vient du serveur. Ne pas le redériver d'un `state === 'past_due'` : c'est
// exactement la recomposition de règle dont ce fichier sort.
export const hasPaymentFailed = (user) => accessOf(user).paymentFailed === true;

// ⚠️ Les deux écrans de paiement restent ouverts alors que l'accès l'est aussi. On s'abonne
// pendant l'essai, sans attendre d'être bloqué, et on renouvelle pendant la période déjà
// payée qui suit une annulation. Les fermer sur `destination` fermerait la porte au moment
// précis où l'entrepreneur a envie de payer.
export const canSubscribe = (user) => accessOf(user).canSubscribe === true;
export const canRenew = (user) => accessOf(user).canRenew === true;

// L'abonnement est vivant : c'est la condition des gestes qui n'ont de sens que sur un
// abonnement en cours, l'annulation et le changement de carte.
//
// ⚠️ « Vivant » couvre le paiement EN SOUFFRANCE, pas seulement l'abonnement qui va bien.
// Le serveur a longtemps répondu sur le seul état « actif » : la section « Paiement » des
// Paramètres disparaissait au moment précis où le paiement venait d'échouer, donc au seul
// moment où elle sert. L'entrepreneur lisait le bandeau « votre dernier paiement n'a pas
// été accepté » et n'avait plus nulle part où mettre sa carte à jour. La règle est
// revenue au serveur (`Organization#live_subscription?`) ; ne pas la refermer ici.
export const hasLiveSubscription = (user) => accessOf(user).canCancel === true;

// A déjà payé au moins une fois, donc a quelque chose à montrer dans son historique. Reste
// vrai après une annulation, contrairement à `hasLiveSubscription`.
export const hasBillingHistory = (user) => accessOf(user).hasBillingHistory === true;
